// eslint-disable-next-line simple-import-sort/imports
import * as API from '@/services';
import { resetToken, setApiAuthorization } from '@/services/api/api.util';
import { persistDeviceToken } from '@/utils';
import { PayloadAction } from '@reduxjs/toolkit';
import { put, select } from 'redux-saga/effects';

import { AuthActions, AuthSelectors, IPayloadOAuthLoginRequest } from '.';
import { CommonActions } from '../common';
import { MyPageActions } from '../my-page';
import {
	IPayloadLoginRequest,
	IPayloadLogoutRequest,
	IPayloadVerifyOtpRequest,
	IPayloadDeviceLoginRequest,
} from './auth.type';

export function* logout(action: PayloadAction<IPayloadLogoutRequest>) {
	const { isCallApi, callback } = action.payload || {};
	try {
		if (isCallApi) {
			yield API.logout();
		}

		const logoutUrl: string = yield select(AuthSelectors.selectLogoutUrl);

		yield put(AuthActions.logoutSuccess());
		yield put(MyPageActions.resetDataMyPage());
		yield put(CommonActions.resetHeaderProgram());
		yield put(CommonActions.setHasFormChange(false));

		callback?.();
		resetToken();
		window.sessionStorage.removeItem('fw_session_end_at');

		if (logoutUrl) {
			window.location.href = logoutUrl;
		}
	} catch (e) {
		callback?.({ error: e });
		yield put(AuthActions.logoutFailure());
	}
}

export function* login(
	action: PayloadAction<IPayloadLoginRequest>
): Generator<any, void, any> {
	const { callback, ...props } = action.payload;

	try {
		// BE may return either a raw JWT string (normal login) or a wrapped
		// object `{ data, typeAccess, preAuthToken, ... }` when MFA/OTP is
		// required. Accept both shapes.
		const res: API.ApiResponseSuccess<API.ApiLoginResponseSuccess | string> =
			yield API.login({ ...props });

		const resData = res.data;

		if (typeof resData === 'string') {
			setApiAuthorization(resData);
			yield put(AuthActions.loginSuccess({ accessToken: resData }));
			callback?.();
			return;
		}

		// OTP required — BE asks for MFA instead of returning an accessToken.
		if (resData.typeAccess === 'EXTERNAL') {
			callback?.({
				otpRequired: true,
				preAuthToken: resData.preAuthToken,
				maskedEmail: resData.maskedEmail,
				expiresAt: resData.expiresAt,
			});
			yield put(AuthActions.loginSuccess({ accessToken: '' }));
			return;
		}

		const accessToken = resData.data;
		setApiAuthorization(accessToken);
		yield put(AuthActions.loginSuccess({ accessToken }));
		callback?.();
	} catch (e) {
		callback?.({ error: e });
		yield put(AuthActions.loginFailure());
	}
}

/**
 * TAB-03 & MOB-04 login — POST /device/client/login. Carries X-Device-Token
 * via the axios interceptor; backend returns a new accessToken + rotated
 * deviceToken, both of which FE must save-override (spec v4.3.4 §6.2).
 */
export function* deviceLogin(
	action: PayloadAction<IPayloadDeviceLoginRequest>
): Generator<any, void, any> {
	const { callback, loginId, password, deviceType, fingerprint } =
		action.payload;

	try {
		const res: { data: API.IDeviceClientLoginResponse } =
			yield API.deviceClientLogin({
				loginId,
				password,
				deviceType,
				fingerprint,
			});

		const { accessToken, deviceToken } = res.data;

		persistDeviceToken(deviceToken);
		setApiAuthorization(accessToken);

		yield put(AuthActions.loginSuccess({ accessToken }));
		callback?.();
	} catch (e) {
		callback?.({ error: e });
		yield put(AuthActions.loginFailure());
	}
}

export function* oauthLogin(
	action: PayloadAction<IPayloadOAuthLoginRequest>
): Generator<any, void, any> {
	const { code, state, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.ApiOAuthTokenResponseSuccess> =
			yield API.oauthLogin(code, state);

		const accessToken = res.data.accessToken;
		const logoutUrl = res.data.logoutUrl;

		setApiAuthorization(accessToken);

		yield put(
			AuthActions.oauthLoginSuccess({
				accessToken,
				logoutUrl,
			})
		);
		callback?.();
	} catch (e) {
		callback?.({ error: e });
		yield put(AuthActions.loginFailure());
	}
}

export function* verifyOtp(
	action: PayloadAction<IPayloadVerifyOtpRequest>
): Generator<any, void, any> {
	const { preAuthToken, otpValue, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<string> = yield API.verifyOtp({
			preAuthToken,
			otpValue,
		});
		// res.data is the accessToken string directly
		const accessToken = res.data;

		// Pass accessToken to callback, let component handle loginSuccess after navigate
		callback?.({ accessToken });
	} catch (e: any) {
		// Extract error data from axios error to ensure it's serializable
		const errorData = e?.response?.data;
		callback?.({
			error: {
				status: e?.response?.status,
				messageCode: errorData?.messageCode,
				message: errorData?.message,
				errorData: errorData?.errorData,
			},
		});
		yield put(AuthActions.loginFailure());
	}
}
